import { Route } from 'vue-router';
import AuthorizationService from '../api/AuthorizationService';
import APIService from '../api';
import {
  IRouterProps,
  TAuthStoreInitializerProp
} from './interfaces/router-props.interface';
import store from '@/store';
import { IBusinessType } from '@/models/business';

const authService = new AuthorizationService();
const OAUTH_SESSION_KEY = '@twism:oauth';
const EXTERNAL_OAUTH_SESSION_KEY = '@twism:external-oauth';
const MERCHANT_INVITE_DATA = '@twism:merchant-invite-data';
const MERCHANT_MEETING_ONBOARDING_DATA =
  '@twism:merchant-meeting-onboarding-data';

export const TRACKING_START_SIGNUP_SESSION_KEY =
  '@twism:started-merchant-signup-flow';

export function hasManagerCredentials(): boolean {
  const credentials = authService.getCredentials();
  return !!credentials;
}

export function hasRootCredentials(): boolean {
  const credentials = authService.getCredentials(true);
  return !!credentials;
}

export function setOauthState(state: any) {
  sessionStorage.setItem(OAUTH_SESSION_KEY, JSON.stringify(state));
}

export function getOauthState(): any {
  const raw = sessionStorage.getItem(OAUTH_SESSION_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function removeOauthState(): any {
  sessionStorage.removeItem(OAUTH_SESSION_KEY);
}

export function isOauthSession(): boolean {
  return !!sessionStorage.getItem(OAUTH_SESSION_KEY);
}

export function setExternalOauthSession(type: IBusinessType) {
  sessionStorage.setItem(EXTERNAL_OAUTH_SESSION_KEY, String(type));
}

export function removeExternalOauthSession(): any {
  sessionStorage.removeItem(EXTERNAL_OAUTH_SESSION_KEY);
}

export function isExternalOauthSession(): boolean {
  return !!sessionStorage.getItem(EXTERNAL_OAUTH_SESSION_KEY);
}

export function isExternalOauthSessionOnline(): boolean {
  return (
    isExternalOauthSession() &&
    sessionStorage.getItem(EXTERNAL_OAUTH_SESSION_KEY) === IBusinessType.ONLINE
  );
}

export function setMerchantMeetingOboardingState(state: any) {
  localStorage.setItem(MERCHANT_MEETING_ONBOARDING_DATA, JSON.stringify(state));
}

export function getMerchantMeetingOboardingState(): any {
  const raw = localStorage.getItem(MERCHANT_MEETING_ONBOARDING_DATA);
  return raw ? JSON.parse(raw) : null;
}

export function removeMerchantMeetingOboardingState(): any {
  localStorage.removeItem(MERCHANT_MEETING_ONBOARDING_DATA);
}

export function setMerchantInviteState(state: any) {
  sessionStorage.setItem(MERCHANT_INVITE_DATA, JSON.stringify(state));
}

export function getMerchantInviteState(): any {
  const raw = sessionStorage.getItem(MERCHANT_INVITE_DATA);
  return raw ? JSON.parse(raw) : null;
}

export function removeMerchantInviteState(): any {
  sessionStorage.removeItem(MERCHANT_INVITE_DATA);
}

export function getQueryStringValue(key: string) {
  return decodeURIComponent(
    window.location.search.replace(
      new RegExp(
        '^(?:.*[&\\?]' +
          encodeURIComponent(key).replace(/[.+*]/g, '\\$&') +
          '(?:\\=([^&]*))?)?.*$',
        'i'
      ),
      '$1'
    )
  );
}

export function getPropsFromRoute(route: Route): IRouterProps {
  // Auth is now cookie-based — the JWT is in an HttpOnly cookie and cannot be
  // decoded client-side. Use APIService.token as a session signal: it is set to
  // 'cookie-session' by initAuthState() when /auth/me succeeds, and '' when not
  // authenticated. This mirrors the old localStorage check so store factories
  // that guard on `context.auth === 'manager'` still skip when unauthenticated.
  const isSuperAdmin = window.location.href.includes('/admin');
  const auth: TAuthStoreInitializerProp = !APIService.token
    ? 'unauthenticated'
    : isSuperAdmin
      ? 'super-admin'
      : 'manager';

  const { matched } = route;

  const getManagerHaveBusiness = () => {
    if (route.path === '/') {
      return false;
    }

    if (matched.length) {
      return matched.every(record => record.meta.requiresBusiness !== false);
    }

    return true;
  };

  return {
    store,
    auth,
    route,
    routeRequirements: {
      managerHaveBusiness: getManagerHaveBusiness(),
      userHaveAuthorization: matched.some(record => record.meta.requiresAuth)
    }
  };
}
